import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';
import { ClipboardCheckIcon, RefreshIcon, PencilIcon, CheckCircleIcon } from '../../components/ui/Icons';

const ActionItems = ({ documents = [] }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);

  const actionItems = useMemo(() => {
    const items = [];
    
    // Generate action items based on document status and age
    documents.forEach(doc => {
      const updatedDate = new Date(doc.updatedAt || doc.createdAt);
      const monthsOld = (new Date() - updatedDate) / (1000 * 60 * 60 * 24 * 30);
      
      // Documents pending review
      if (doc.status?.name === 'Pending' || doc.status?.name === 'Review') {
        items.push({
          id: `review-${doc.id}`,
          title: `Review ${doc.title || `Document ${doc.id}`}`,
          description: `Document requires review and approval`,
          priority: 'Medium',
          type: 'Review',
          assignee: doc.updatedBy?.userDisplayName || 'Unassigned',
          department: doc.department?.name || 'Unknown',
          dueDate: new Date(updatedDate.getTime() + 7 * 24 * 60 * 60 * 1000), // 7 days from last update
          documentId: doc.id,
          documentCode: doc.code
        });
      }
      
      // Old documents that might need updating
      if (monthsOld > 11 && doc.status?.name === 'Active') {
        items.push({
          id: `update-${doc.id}`,
          title: `Update ${doc.title || `Document ${doc.id}`}`,
          description: `Document hasn't been updated in ${Math.floor(monthsOld)} months`,
          priority: monthsOld > 18 ? 'High' : 'Low',
          type: 'Update',
          assignee: doc.updatedBy?.userDisplayName || 'Unassigned',
          department: doc.department?.name || 'Unknown',
          dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
          documentId: doc.id,
          documentCode: doc.code
        });
      }
      
      // Draft documents
      if (doc.status?.name === 'Draft') {
        const createdDate = new Date(doc.createdAt);
        const daysOld = (new Date() - createdDate) / (1000 * 60 * 60 * 24);
        
        if (daysOld > 7) {
          items.push({
            id: `complete-${doc.id}`,
            title: `Complete ${doc.title || `Document ${doc.id}`}`,
            description: `Draft document has been in progress for ${Math.floor(daysOld)} days`,
            priority: daysOld > 30 ? 'High' : 'Medium',
            type: 'Complete',
            assignee: doc.createdBy?.userDisplayName || 'Unassigned',
            department: doc.department?.name || 'Unknown',
            dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
            documentId: doc.id,
            documentCode: doc.code
          });
        }
      }
    });
    
    // Sort by priority and due date
    return items
      .sort((a, b) => {
        const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return new Date(a.dueDate) - new Date(b.dueDate);
      })
      .slice(0, 8); // Show top 8 items
  }, [documents]);

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return dash.fill('danger');
      case 'medium':
        return dash.fill('warning');
      case 'low':
        return dash.fill('success');
      default:
        return dash.fill('neutral');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Review':
        return <ClipboardCheckIcon className="w-4 h-4" />;
      case 'Update':
        return <RefreshIcon className="w-4 h-4" />;
      case 'Complete':
        return <PencilIcon className="w-4 h-4" />;
      default:
        return <ClipboardCheckIcon className="w-4 h-4" />;
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const days = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Overdue';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days} days left`;
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  return (
    <div className={`${dash.widgetShell('h-full')}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
          Action Items
        </h3>
        <div className={dash.pill('warning', 'gap-2')}>
          <div className={`w-2 h-2 rounded-full ${dash.fill('warning')}`}></div>
          <span className={`text-sm font-medium ${dash.text('warning')}`}>
            {actionItems.filter(item => item.priority === 'High').length} High Priority
          </span>
        </div>
      </div>

      {actionItems.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <CheckCircleIcon className={`w-12 h-12 mx-auto mb-4 ${theme.text.muted}`} />
            <p className={`${theme.text.muted}`}>All caught up!</p>
            <p className={`text-sm ${theme.text.muted} mt-1`}>No action items at this time</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {actionItems.map((item) => (
            <div 
              key={item.id}
              className={`p-4 rounded-lg ${dash.listItem()} transition-colors duration-200 border ${theme.border.light} ${
                isOverdue(item.dueDate) ? (dash.isRefined ? 'ring-1 ring-rose-300 dark:ring-rose-700' : 'ring-1 ring-red-400') : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 p-2 ${getPriorityColor(item.priority)} bg-opacity-10 rounded-lg`}>
                  <div className={getPriorityColor(item.priority).replace('bg-', 'text-')}>
                    {getTypeIcon(item.type)}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className={`text-sm font-medium ${theme.text.primary} truncate pr-2`}>
                      {item.title}
                      {item.documentCode && (
                        <span className={`ml-2 text-xs ${theme.text.muted}`}>
                          ({item.documentCode})
                        </span>
                      )}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)} bg-opacity-10 ${getPriorityColor(item.priority).replace('bg-', 'text-')}`}>
                      {item.priority}
                    </span>
                  </div>
                  
                  <p className={`text-xs ${theme.text.secondary} mb-2`}>
                    {item.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={`text-xs ${theme.text.muted}`}>
                        <span className="font-medium">Assignee:</span> {item.assignee}
                      </span>
                      <span className={`text-xs ${theme.text.muted}`}>
                        {item.department}
                      </span>
                    </div>
                    <span className={`text-xs font-medium ${
                      isOverdue(item.dueDate) 
                        ? dash.text('danger')
                        : theme.text.secondary
                    }`}>
                      {getDaysUntilDue(item.dueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className={`mt-6 pt-4 border-t ${theme.border.light}`}>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className={`text-lg font-bold ${theme.text.primary}`}>
              {actionItems.filter(item => item.priority === 'High').length}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>High Priority</div>
          </div>
          <div>
            <div className={`text-lg font-bold ${theme.text.primary}`}>
              {actionItems.filter(item => isOverdue(item.dueDate)).length}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>Overdue</div>
          </div>
          <div>
            <div className={`text-lg font-bold ${theme.text.primary}`}>
              {actionItems.filter(item => item.type === 'Review').length}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>Reviews</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionItems;
